import type { ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import '@/styles/record.css'

/**
 * Четыре «пустых» состояния (CompStates.dc.html).
 *
 * Различаются **словом-меткой** над заголовком и иконкой, а не только текстом: «нет данных»,
 * «нет доступа», «не настроено» и «ошибка» требуют разных действий, и пустота, одинаковая
 * для всех четырёх, отправляет пользователя в поддержку с работающим запретом.
 */
export type StateKind = 'empty' | 'forbidden' | 'setup' | 'error'

const ICONS: Record<StateKind, IconName> = {
  empty: 'inbox',
  forbidden: 'lock',
  setup: 'settings',
  error: 'alert',
}

const MODIFIERS: Record<StateKind, string> = {
  empty: '',
  forbidden: 'is-forbidden',
  setup: 'is-setup',
  error: 'is-error',
}

export interface StateViewProps {
  kind: StateKind
  /** Что случилось — одной фразой («Событий пока нет»). */
  title: ReactNode
  /** Причина и что из неё следует. */
  description?: ReactNode
  /** Следующее действие: кнопки или ссылки. */
  actions?: ReactNode
  /** Технический след ошибки (код, номер запроса) — для поддержки. */
  code?: ReactNode
  /** Внутри панели или виджета: меньше воздуха, без своей рамки. */
  compact?: boolean
  /** Своя рамка (`.state-box`) — когда состояние занимает место экрана. */
  boxed?: boolean
}

export function StateView({ kind, title, description, actions, code, compact, boxed }: StateViewProps) {
  const { t } = useApp()
  const classes = ['state', MODIFIERS[kind]].filter(Boolean).join(' ')
  const body = (
    <div
      className={classes}
      // Ошибка объявляется сразу, остальное — вежливо: «нет данных» не повод прерывать чтение.
      role={kind === 'error' ? 'alert' : 'status'}
      style={compact ? { padding: '28px 16px' } : undefined}
    >
      <span className="state-kind">{t(`state.kind.${kind}`)}</span>
      {!compact && (
        <span className="state-ico">
          <Icon name={ICONS[kind]} />
        </span>
      )}
      <span className="st">{title}</span>
      {description && <span className="sd">{description}</span>}
      {actions && <span className="sa">{actions}</span>}
      {code && <span className="code">{code}</span>}
    </div>
  )
  return boxed ? <div className="state-box">{body}</div> : body
}
